<template>
    <div class="dialog_area">
        <div class="setting_box">
            <div class="box_title">Googleスプレッドシート連携</div>
            <div class="alert_box">
                <p>申込完了後の情報を Google スプレッドシートへ出力するための設定です。</p>
                <p>顧客側でスプレッドシートを作成し、サービスアカウントのメールアドレスに編集権限で共有してください。</p>
            </div>

            <div class="form_row switch_row">
                <label class="checkbox_label">
                    <input type="checkbox" v-model="settings.enabled" />
                    <span>Googleスプレッドシート連携を有効にする</span>
                </label>
            </div>

            <div class="form_row">
                <label class="form_label" for="spreadsheetUrl">スプレッドシートURL</label>
                <input id="spreadsheetUrl" v-model.trim="settings.spreadsheetUrl" type="text" class="text_input"
                    :disabled="busy || !settings.enabled"
                    placeholder="https://docs.google.com/spreadsheets/d/.../edit" />
            </div>

            <div class="form_row split_row">
                <div class="split_item">
                    <label class="form_label" for="sheetName">シート名</label>
                    <input id="sheetName" v-model.trim="settings.sheetName" type="text" class="text_input"
                        :disabled="busy || !settings.enabled" placeholder="申込者一覧" />
                </div>
                <div class="split_item">
                    <label class="form_label" for="serviceAccountEmail">共有先メールアドレス</label>
                    <div class="input_action_row">
                        <input
                            id="serviceAccountEmail"
                            ref="serviceAccountEmailInput"
                            v-model.trim="settings.serviceAccountEmail"
                            type="text"
                            class="text_input"
                            readonly
                            placeholder="サービスアカウント作成後に表示されます"
                            @click="selectServiceAccountEmail"
                            @focus="selectServiceAccountEmail"
                        />
                        <button
                            type="button"
                            class="copy_button"
                            :disabled="!settings.serviceAccountEmail"
                            @click="copyServiceAccountEmail"
                        >
                            コピー
                        </button>
                    </div>
                </div>
            </div>

            <div class="button_row">
                <button class="action_button secondary" :disabled="busy" @click="loadGoogleSheetSetting">再読込</button>
                <button class="action_button secondary" :disabled="busy" @click="testGoogleSheetSetting">接続確認</button>
                <button class="action_button primary" :disabled="busy" @click="saveGoogleSheetSetting">保存</button>
            </div>

            <div class="status_box" :class="statusClass">
                <div class="status_line"><strong>接続状態:</strong> {{ statusLabel }}</div>
                <div v-if="statusMessage" class="status_message">{{ statusMessage }}</div>
            </div>
        </div>

        <div class="answer_box">
            <div class="answer_header">
                <div class="box_title">申込者一覧</div>
                <button class="action_button dark" @click="downloadCSV">CSVダウンロード</button>
            </div>

            <div v-if="answer.length === 0" class="empty_message">申込データはまだありません。</div>
            <table v-else align="center">
                <tr v-for="(raw, index) in answer" :key="index">
                    <td v-for="(col, cindex) in raw" :key="cindex">{{ col }}</td>
                </tr>
            </table>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Api from '@/lib/Api.js'
import { encode } from 'iconv-lite';

export default {
    name: 'dialogChatbottag',
    props: ['id'],
    components: {
    },
    data() {
        return {
            answer: [],
            visible: false,
            busy: false,
            settings: {
                enabled: false,
                spreadsheetUrl: '',
                sheetName: '申込者一覧',
                serviceAccountEmail: '',
                lastTestStatus: 'untested',
                lastTestMessage: '',
            },
            saveMessage: '',
        }
    },
    computed: {
        ...mapGetters({
            customer_id: 'customer/customer_id',
            scenario_id: 'scenario/id',
            scenario_tag: 'scenario/tag',
        }),
        statusLabel() {
            const map = {
                success: '接続成功',
                error: '接続失敗',
                untested: '未確認',
                testing: '確認中',
                saving: '保存中',
            }
            return map[this.settings.lastTestStatus] || '未確認'
        },
        statusMessage() {
            return this.saveMessage || this.settings.lastTestMessage
        },
        statusClass() {
            return {
                success: this.settings.lastTestStatus === 'success',
                error: this.settings.lastTestStatus === 'error',
            }
        },
    },
    created: async function () {
        await Promise.all([
            this.loadAnswers(),
            this.loadGoogleSheetSetting(),
        ])
    },
    mounted: function () {
        this.visible = true
    },
    methods: {
        async loadAnswers() {
            let response = await Api.answerGetListAll(this, this.scenario_id)
            this.answer = response?.data || []
        },
        normalizeSettingResponse(response) {
            const data = response?.data || {}
            const responseStatus = response?.status || ''
            this.settings.enabled = Boolean(Number(data.enabled ?? 0))
            this.settings.spreadsheetUrl = data.spreadsheet_url || data.spreadsheetUrl || ''
            this.settings.sheetName = data.sheet_name || data.sheetName || '申込者一覧'
            this.settings.serviceAccountEmail = data.service_account_email || data.serviceAccountEmail || ''
            this.settings.lastTestStatus = data.last_test_status || data.lastTestStatus || (responseStatus === 'OK' ? 'untested' : 'error')
            this.settings.lastTestMessage = data.last_test_message || data.lastTestMessage || response?.message || ''
        },
        validateSettings(checkEnabled = true) {
            this.saveMessage = ''
            if (checkEnabled && !this.settings.enabled) {
                return true
            }
            if (!this.settings.spreadsheetUrl) {
                alert('スプレッドシートURLを入力してください。')
                return false
            }
            if (!/^https:\/\/docs\.google\.com\/spreadsheets\/d\/[a-zA-Z0-9-_]+/.test(this.settings.spreadsheetUrl)) {
                alert('GoogleスプレッドシートのURL形式で入力してください。')
                return false
            }
            if (!this.settings.sheetName) {
                alert('シート名を入力してください。')
                return false
            }
            return true
        },
        async loadGoogleSheetSetting() {
            this.busy = true
            this.saveMessage = ''
            try {
                const response = await Api.googleSheetGet(this, { id: this.scenario_id })
                if (response?.status === 'OK') {
                    this.normalizeSettingResponse(response)
                } else {
                    this.settings.lastTestStatus = 'untested'
                    this.settings.lastTestMessage = response?.message || 'Googleスプレッドシート設定はまだ保存されていません。'
                    if (response?.data) {
                        this.normalizeSettingResponse(response)
                    }
                }
            } finally {
                this.busy = false
            }
        },
        async saveGoogleSheetSetting() {
            if (!this.validateSettings()) {
                return
            }
            this.busy = true
            this.saveMessage = ''
            this.settings.lastTestStatus = 'saving'
            try {
                const response = await Api.googleSheetSave(this, {
                    id: this.scenario_id,
                    enabled: this.settings.enabled ? 1 : 0,
                    spreadsheetUrl: this.settings.spreadsheetUrl,
                    sheetName: this.settings.sheetName,
                })
                if (response?.status === 'OK') {
                    this.saveMessage = response?.message || 'Googleスプレッドシート連携設定を保存しました。'
                    if (response?.data) {
                        this.normalizeSettingResponse(response)
                    }
                    this.$store.commit('scenario/setSaved', { saved: true })
                } else {
                    this.settings.lastTestStatus = 'error'
                    this.saveMessage = response?.message || 'Googleスプレッドシート連携設定の保存に失敗しました。'
                }
            } finally {
                this.busy = false
            }
        },
        async testGoogleSheetSetting() {
            if (!this.validateSettings(false)) {
                return
            }
            this.busy = true
            this.saveMessage = ''
            this.settings.lastTestStatus = 'testing'
            this.settings.lastTestMessage = '接続確認を実行しています。'
            try {
                const response = await Api.googleSheetTest(this, {
                    id: this.scenario_id,
                    spreadsheetUrl: this.settings.spreadsheetUrl,
                    sheetName: this.settings.sheetName,
                })
                if (response?.status === 'OK') {
                    this.settings.lastTestStatus = 'success'
                    this.settings.lastTestMessage = response?.message || response?.data?.message || '接続成功'
                    if (response?.data?.service_account_email || response?.data?.serviceAccountEmail) {
                        this.settings.serviceAccountEmail = response.data.service_account_email || response.data.serviceAccountEmail
                    }
                } else {
                    this.settings.lastTestStatus = 'error'
                    this.settings.lastTestMessage = response?.message || '接続確認に失敗しました。'
                }
            } finally {
                this.busy = false
            }
        },
        downloadCSV() {
            let date = new Date()
            let dateStr = date.getFullYear() + ('0' + (date.getMonth() + 1)).slice(-2) + ('0' + date.getDate()).slice(-2) + ('0' + date.getHours()).slice(-2) + ('0' + date.getMinutes()).slice(-2) + ('0' + date.getSeconds()).slice(-2)

            let csv = ''
            this.answer.forEach(r => {
                let sep = ''
                r.forEach(c => {
                    const value = c == null ? '' : String(c)
                    csv += sep + '"' + value.replaceAll('"', '""') + '"'
                    sep = ','
                })
                csv += "\r\n"
            })
            const encoded = encode(csv, 'Shift_JIS');
            let blob = new Blob([encoded], { type: 'text/plain' });
            let blobURL = window.URL.createObjectURL(blob);
            let obj = document.createElement('a');
            obj.href = blobURL;
            obj.download = `scenario${this.scenario_id}_${dateStr}.csv`
            document.body.appendChild(obj);
            obj.click();
            obj.parentNode.removeChild(obj);
        },

        selectServiceAccountEmail() {
            this.$nextTick(() => {
                const input = this.$refs.serviceAccountEmailInput
                if (input && typeof input.focus === 'function' && typeof input.select === 'function') {
                    input.focus()
                    input.select()
                }
            })
        },
        async copyServiceAccountEmail() {
            const value = this.settings.serviceAccountEmail || ''
            if (!value) {
                return
            }

            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(value)
                } else {
                    const input = this.$refs.serviceAccountEmailInput
                    if (input && typeof input.focus === 'function' && typeof input.select === 'function') {
                        input.focus()
                        input.select()
                        document.execCommand('copy')
                    } else {
                        throw new Error('copy fallback failed')
                    }
                }
                this.saveMessage = '共有先メールアドレスをコピーしました。'
            } catch (e) {
                this.saveMessage = '共有先メールアドレスのコピーに失敗しました。'
            }
        },
    },
}
</script>

<style scoped>
.dialog_area {
    width: calc(100% - 30px);
    margin: 0 15px;
    background-color: white;
    height: 100%;
    top: 0;
    left: 0;
    overflow-y: auto;
    z-index: 3;
    padding: 24px 24px 40px;
    box-sizing: border-box;
}

.input_action_row {
    display: flex;
    gap: 10px;
    align-items: center;
}

.copy_button {
    outline: none;
    border: none;
    color: white;
    background-color: #42B5D1;
    padding: 10px 16px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
    white-space: nowrap;
}

.copy_button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.setting_box,
.answer_box {
    background-color: white;
    border: 1px solid #d9e3ea;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(66, 181, 209, 0.08);
}

.answer_box {
    margin-top: 24px;
}

.box_title {
    font-size: 20px;
    font-weight: 700;
    color: #333;
    margin-bottom: 20px;
}

.alert_box {
    box-sizing: border-box;
    color: white;
    background-color: #55b8d2;
    border-radius: 10px;
    padding: 14px 20px;
    margin-bottom: 24px;
}

.alert_box p {
    margin: 6px 0;
}

.form_row {
    margin-bottom: 18px;
}

.switch_row {
    margin-bottom: 20px;
}

.checkbox_label {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 700;
    color: #333;
}

.form_label {
    display: block;
    font-weight: 700;
    color: #333;
    margin-bottom: 8px;
}

.text_input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #cbd5dc;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
}

.text_input:disabled {
    background-color: #f4f6f8;
    color: #666;
}

.split_row {
    display: flex;
    gap: 16px;
}

.split_item {
    flex: 1;
}

.button_row {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    flex-wrap: wrap;
}

.action_button {
    outline: none;
    border: none;
    color: white;
    padding: 10px 24px;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 700;
}

.action_button.primary {
    background-color: #42B5D1;
}

.action_button.secondary {
    background-color: #7c8b95;
}

.action_button.dark {
    background-color: #6c6b6b;
}

.action_button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.status_box {
    margin-top: 20px;
    background-color: #f5f7fa;
    border: 1px solid #d9e3ea;
    border-radius: 8px;
    padding: 16px;
    color: #333;
}

.status_box.success {
    background-color: #eefaf4;
    border-color: #99d1b1;
}

.status_box.error {
    background-color: #fff2f2;
    border-color: #f0b2b2;
}

.status_line {
    margin-bottom: 6px;
}

.status_message {
    white-space: pre-wrap;
    word-break: break-word;
}

.answer_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
}

.empty_message {
    padding: 24px;
    background-color: #f7f9fb;
    border-radius: 8px;
    color: #666;
    text-align: center;
}

table {
    width: 100%;
    border-spacing: 0;
    border: 1px solid black;
    table-layout: fixed;
}

td {
    border: 1px solid black;
    word-break: break-all;
    padding: 6px;
}

@media (max-width: 1100px) {
    .split_row {
        flex-direction: column;
    }

    .answer_header {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>